// src/components/OpenBriefModal.jsx
//
// Quick-view "Open brief" modal, ported from the demo environment. Renders a
// read-only summary of one opportunity: score, product fit, estimated value,
// why-now context, signals, contact details and the current AI draft.
// Actions stay out of the modal; "Open full opportunity" takes the rep to the
// existing opportunity card where drafts can be generated, approved and sent.
import React, { useEffect } from "react";

const gbp = (value) =>
  value != null
    ? `£${Math.round(Number(value)).toLocaleString("en-GB")}`
    : "—";

const titleCase = (value) =>
  (value || "")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const styles = `
  .ob-backdrop { position: fixed; inset: 0; background: rgba(4,10,22,0.5); backdrop-filter: blur(3px); z-index: 2200; display: flex; align-items: center; justify-content: center; padding: 20px; }
  .ob-modal { width: min(760px, 96vw); max-height: 88vh; overflow: auto; background: var(--surface); border: 1px solid var(--border); border-radius: 14px; box-shadow: 0 24px 64px rgba(0,0,0,0.22); font-family: var(--sans); color: var(--text); }
  .ob-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; padding: 20px 22px; border-bottom: 1px solid var(--border); position: sticky; top: 0; z-index: 2; background: var(--surface); }
  .ob-eyebrow { display: inline-flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: 9999px; background: var(--surface-sunken); font-size: 11px; font-weight: 600; color: var(--text-muted); }
  .ob-title { margin-top: 8px; font-size: 18px; font-weight: 600; letter-spacing: -0.02em; line-height: 1.3; color: var(--text); }
  .ob-sub { margin-top: 3px; font-size: 12.5px; color: var(--text-muted); }
  .ob-close { width: 30px; height: 30px; border-radius: 8px; border: 1px solid var(--border); background: var(--surface-raised); color: var(--text-muted); cursor: pointer; font-size: 17px; line-height: 1; flex-shrink: 0; }
  .ob-body { padding: 20px 22px; display: flex; flex-direction: column; gap: 16px; }
  .ob-kv-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; }
  .ob-kv { background: var(--surface-sunken); border-radius: var(--radius); padding: 10px 12px; min-width: 0; }
  .ob-kv-label { font-size: 10px; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-faint); }
  .ob-kv-value { margin-top: 4px; font-size: 14px; font-weight: 600; color: var(--text); }
  .ob-section-title { font-size: 13px; font-weight: 600; color: var(--text); margin-bottom: 7px; }
  .ob-copy { background: var(--surface-sunken); border-radius: var(--radius); padding: 13px 15px; font-size: 13px; line-height: 1.65; color: var(--text-muted); white-space: pre-wrap; }
  .ob-copy strong { color: var(--text); }
  .ob-signal-list { display: flex; flex-direction: column; gap: 6px; }
  .ob-signal { display: flex; gap: 8px; align-items: flex-start; font-size: 12.5px; line-height: 1.55; color: var(--text-muted); }
  .ob-signal::before { content: ""; width: 5px; height: 5px; border-radius: 50%; background: var(--accent-mid); margin-top: 6px; flex-shrink: 0; }
  .ob-contact-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 9px 0; border-bottom: 1px solid var(--border); font-size: 12.5px; color: var(--text-muted); }
  .ob-contact-row:last-child { border-bottom: none; }
  .ob-contact-row b { color: var(--text); font-weight: 500; overflow-wrap: anywhere; text-align: right; }
  .ob-chip { display: inline-flex; align-items: center; gap: 5px; padding: 2px 8px; border-radius: 9999px; background: var(--surface-sunken); font-size: 10.5px; font-weight: 500; color: var(--text); }
  .ob-chip::before { content: ""; width: 5px; height: 5px; border-radius: 50%; background: var(--dot, var(--text-faint)); }
  .ob-chip.good { --dot: var(--green); }
  .ob-chip.warn { --dot: var(--amber); }
  .ob-chip.bad { --dot: var(--red); }
  .ob-draft { background: var(--surface-sunken); border-radius: var(--radius); padding: 13px 15px; }
  .ob-draft-subject { font-size: 13px; font-weight: 600; color: var(--text); margin-bottom: 6px; }
  .ob-draft-body { white-space: pre-wrap; font-size: 12.5px; line-height: 1.65; color: var(--text-muted); max-height: 220px; overflow: auto; }
  .ob-empty { padding: 13px 15px; border: 1px dashed var(--border); border-radius: var(--radius); font-size: 12.5px; line-height: 1.55; color: var(--text-muted); }
  .ob-loading { font-size: 12.5px; color: var(--text-faint); }
  .ob-foot { display: flex; justify-content: flex-end; gap: 8px; padding: 14px 22px; border-top: 1px solid var(--border); background: var(--surface-raised); position: sticky; bottom: 0; }
  .ob-btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 34px; padding: 0 14px; border-radius: 8px; border: 1px solid var(--border); background: var(--surface); color: var(--text); font-family: var(--sans); font-size: 12.5px; font-weight: 500; cursor: pointer; }
  .ob-btn:hover { background: var(--surface-sunken); }
  .ob-btn.primary { background: var(--accent); border-color: var(--accent); color: #fff; }
  .ob-btn.primary:hover { background: var(--accent-hover); }
  @media (max-width: 640px) {
    .ob-backdrop { padding: 10px; }
    .ob-head, .ob-body, .ob-foot { padding-left: 16px; padding-right: 16px; }
    .ob-kv-grid { grid-template-columns: 1fr 1fr; }
  }
`;

export default function OpenBriefModal({
  opportunity,
  productLabel,
  stageLabel,
  nextStep,
  briefText,
  signals = [],
  contact,
  draft,
  loading = false,
  onClose,
  onOpenFull,
}) {
  useEffect(() => {
    if (!opportunity) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [opportunity, onClose]);

  if (!opportunity) return null;

  const score = opportunity.priorityScore;
  const emailValid = contact?.emailValidation?.valid;
  const contactRows = contact
    ? [
        ["Email", contact.email || "Not on file"],
        ["Phone", contact.phone || "Not on file"],
        [
          "Email status",
          contact.email
            ? emailValid
              ? "Verified"
              : contact.emailValidation?.reason || "Not verified"
            : "—",
        ],
        ["LinkedIn", contact.linkedinUrl || "Not on file"],
      ]
    : [];

  return (
    <>
      <style>{styles}</style>
      <div className="ob-backdrop" onClick={onClose}>
        <div
          className="ob-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="open-brief-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="ob-head">
            <div>
              <span className="ob-eyebrow">AI company brief</span>
              <div className="ob-title" id="open-brief-title">
                {opportunity.companyName || "Unknown company"}
              </div>
              <div className="ob-sub">
                {[stageLabel, productLabel, opportunity.companyNumber]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
            <button className="ob-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>

          <div className="ob-body">
            <div className="ob-kv-grid">
              <div className="ob-kv">
                <div className="ob-kv-label">Priority score</div>
                <div className="ob-kv-value">
                  {score != null ? `${score}/100` : "—"}
                </div>
              </div>
              <div className="ob-kv">
                <div className="ob-kv-label">Recommended product</div>
                <div className="ob-kv-value">{productLabel || "—"}</div>
              </div>
              <div className="ob-kv">
                <div className="ob-kv-label">Estimated value</div>
                <div className="ob-kv-value">
                  {gbp(opportunity.estimatedValueGbp)}
                </div>
              </div>
              <div className="ob-kv">
                <div className="ob-kv-label">Pipeline stage</div>
                <div className="ob-kv-value">{stageLabel || "—"}</div>
              </div>
            </div>

            {briefText && (
              <div>
                <div className="ob-section-title">Why this account, now</div>
                <div className="ob-copy">{briefText}</div>
              </div>
            )}

            {nextStep && (
              <div>
                <div className="ob-section-title">Recommended next step</div>
                <div className="ob-copy">{nextStep}</div>
              </div>
            )}

            {signals.length > 0 && (
              <div>
                <div className="ob-section-title">Signals</div>
                <div className="ob-signal-list">
                  {signals.slice(0, 5).map((signal, i) => (
                    <div className="ob-signal" key={`${signal}-${i}`}>
                      {signal}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="ob-section-title">Key contact</div>
              {loading ? (
                <div className="ob-loading">
                  Loading contact details and drafts…
                </div>
              ) : contact ? (
                <div>
                  {contactRows.map(([label, value]) => (
                    <div className="ob-contact-row" key={label}>
                      <span>{label}</span>
                      <b>{value}</b>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="ob-empty">
                  Contact details have not been loaded yet. Open the full
                  opportunity to find or verify the contact.
                </div>
              )}
            </div>

            <div>
              <div className="ob-section-title">AI outreach draft</div>
              {draft ? (
                <div className="ob-draft">
                  <div className="ob-draft-subject">
                    {draft.subject || titleCase(draft.draftType) || "Draft"}
                  </div>
                  <div className="ob-draft-body">
                    {draft.content || draft.body || "No content yet."}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginTop: 10,
                    }}
                  >
                    <span
                      className={`ob-chip ${
                        draft.status === "sent"
                          ? "good"
                          : draft.status === "draft"
                            ? "warn"
                            : ""
                      }`}
                    >
                      {titleCase(draft.status || "draft")}
                    </span>
                    <span style={{ fontSize: 11, color: "var(--text-faint)" }}>
                      Review, edit and send from the full opportunity.
                    </span>
                  </div>
                </div>
              ) : (
                <div className="ob-empty">
                  No outreach draft prepared yet. Open the full opportunity to
                  generate one.
                </div>
              )}
            </div>
          </div>

          <div className="ob-foot">
            <button className="ob-btn" onClick={onClose}>
              Close
            </button>
            <button
              className="ob-btn primary"
              onClick={() => {
                onClose?.();
                onOpenFull?.(opportunity);
              }}
            >
              Open full opportunity →
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
